import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ArrowRight, Home, Hammer, Building2, ShieldCheck, ClipboardCheck, FileText, Sparkles } from 'lucide-react';
import { api } from '../lib/api';
import Header from '../components/Header';
import SmartSearchHelper from '../components/SmartSearchHelper';

/**
 * Trades & Neighbours — Three-Door Landing
 * Australia's public marketplace for finding trusted local tradies —
 * powered by Property Passport, the professional maintenance platform.
 */
export default function Landing() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [suburb, setSuburb] = useState('');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.categories().then((r) => setCategories(r.data.slice(0, 8))).catch(() => {});
  }, []);

  const doSearch = (e) => {
    e?.preventDefault();
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (suburb) params.set('suburb', suburb);
    navigate(`/search?${params.toString()}`);
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="landing-page">
      <Header />
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden border-b border-[#E5E4E0]">
        <div className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 border border-[#1A362D] bg-[#1A362D]/[0.05] px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.28em] text-[#1A362D]" data-testid="hero-badge">
              <Sparkles size={12} /> Australia · Property Maintenance · Reimagined
            </div>
            <h1 className="mt-6 font-heading text-5xl font-bold leading-[1.02] tracking-tight text-[#1C1B1A] md:text-7xl lg:text-[5.5rem]" data-testid="hero-title">
              Trades <span className="text-[#1A362D]">&amp;</span> Neighbours.
              <br />
              <span className="italic text-[#5C5B5A]">One record. Every property.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-[#5C5B5A]" data-testid="hero-subtitle">
              Australia's public marketplace for finding trusted local tradies — powered by <strong className="font-semibold text-[#1C1B1A]">Property Passport</strong>, the professional maintenance platform used by strata, real estate agencies and councils behind the scenes.
            </p>
            <p className="mt-4 max-w-2xl text-sm font-medium uppercase tracking-[0.2em] text-[#8A8988]">
              Simple for homeowners · Powerful for professionals · One platform · Different doors
            </p>
          </div>

          {/* Quick search */}
          <form onSubmit={doSearch} className="mt-12 grid max-w-4xl grid-cols-1 gap-3 border border-[#1C1B1A] bg-white p-3 md:grid-cols-[1.4fr,1fr,auto]" data-testid="hero-search-form">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="What do you need? Try 'dripping tap' or 'my roof is leaking'…"
              className="border-0 border-b border-[#E5E4E0] bg-transparent px-3 py-3 text-sm outline-none focus:border-[#1A362D] md:border-b-0 md:border-r"
              data-testid="hero-search-what"
            />
            <input
              value={suburb}
              onChange={(e) => setSuburb(e.target.value)}
              placeholder="Suburb (Fremantle, Cottesloe…)"
              className="border-0 bg-transparent px-3 py-3 text-sm outline-none focus:bg-[#F3F2EE]"
              data-testid="hero-search-suburb"
            />
            <button type="submit" className="btn-primary" data-testid="hero-search-btn">
              Find a tradie <ArrowRight size={16} />
            </button>
          </form>

          {/* Smart Search Helper — inclusive plain-English matcher */}
          <SmartSearchHelper query={q} suburb={suburb} />
        </div>
      </section>

      {/* ============ THREE DOORS ============ */}
      <section className="border-b border-[#E5E4E0] bg-white py-20">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <div className="mb-14 max-w-3xl">
            <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#D96C4E]">One platform · Three doors</div>
            <h2 className="mt-3 font-heading text-4xl font-bold tracking-tight text-[#1C1B1A] md:text-5xl">
              Whoever you are, you have a way in.
            </h2>
            <p className="mt-4 text-base text-[#5C5B5A]">
              We built one shared record for every Australian property. Then we gave each audience the door that fits them best. Same data underneath. Different experiences on top.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Door 1: Homeowners */}
            <Link to="/search" className="group flex flex-col justify-between border border-[#E5E4E0] bg-[#FAF9F6] p-8 transition-all hover:border-[#1A362D] hover:shadow-lg" data-testid="door-homeowners">
              <div>
                <div className="flex h-12 w-12 items-center justify-center bg-[#1A362D] text-white">
                  <Home size={22} strokeWidth={2} />
                </div>
                <h3 className="mt-6 font-heading text-2xl font-bold text-[#1C1B1A]">For Homeowners</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#5C5B5A]">
                  Find trusted local tradies. Request quotes. Keep a permanent maintenance history of your home — private, portable, and yours to share when you sell.
                </p>
                <ul className="mt-5 space-y-2 text-sm text-[#1C1B1A]">
                  <li>· Free forever</li>
                  <li>· Verified tradies in your suburb</li>
                  <li>· Your home's maintenance history in one place</li>
                </ul>
              </div>
              <div className="mt-8 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-[#1A362D] group-hover:gap-3">
                Find a tradie <ArrowRight size={14} />
              </div>
            </Link>

            {/* Door 2: Tradies */}
            <Link to="/for-tradies" className="group flex flex-col justify-between border border-[#E5E4E0] bg-[#1A362D] p-8 text-white transition-all hover:shadow-xl" data-testid="door-tradies">
              <div>
                <div className="flex h-12 w-12 items-center justify-center bg-white text-[#1A362D]">
                  <Hammer size={22} strokeWidth={2} />
                </div>
                <h3 className="mt-6 font-heading text-2xl font-bold text-white">For Tradies</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/80">
                  Win more work. Manage jobs, quotes and invoices in one place. Build your professional reputation. Access the private <strong>Risk &amp; Trust</strong> log to check clients before you quote.
                </p>
                <ul className="mt-5 space-y-2 text-sm text-white">
                  <li>· Free to list, forever</li>
                  <li>· Pro tools from $49/mo</li>
                  <li>· Private client conduct log (Pro)</li>
                </ul>
              </div>
              <div className="mt-8 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-white group-hover:gap-3">
                List your trade <ArrowRight size={14} />
              </div>
            </Link>

            {/* Door 3: Property Passport */}
            <Link to="/passport" className="group flex flex-col justify-between border border-[#1C1B1A] bg-[#1C1B1A] p-8 text-white transition-all hover:shadow-xl" data-testid="door-passport">
              <div>
                <div className="flex h-12 w-12 items-center justify-center bg-[#D96C4E] text-white">
                  <Building2 size={22} strokeWidth={2} />
                </div>
                <div className="mt-6 text-[10px] font-semibold uppercase tracking-[0.28em] text-[#D96C4E]">Property Passport</div>
                <h3 className="mt-2 font-heading text-2xl font-bold text-white">For Professionals</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/70">
                  Strata, real estate, councils &amp; facilities managers. Every property, every job, every document — one workspace. Portfolio dashboards, compliance, audit trails, contractor panels.
                </p>
                <ul className="mt-5 space-y-2 text-sm text-white">
                  <li>· Portfolio-level dashboards</li>
                  <li>· Full audit trail</li>
                  <li>· From $299/mo</li>
                </ul>
              </div>
              <div className="mt-8 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-[#D96C4E] group-hover:gap-3">
                See Property Passport <ArrowRight size={14} />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* ============ THE ONE WORKFLOW ============ */}
      <section className="border-b border-[#E5E4E0] bg-[#FAF9F6] py-20">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <div className="mb-12 max-w-3xl">
            <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#1A362D]">The workflow we obsess over</div>
            <h2 className="mt-3 font-heading text-4xl font-bold tracking-tight text-[#1C1B1A] md:text-5xl">
              One job. One workspace. One source of truth.
            </h2>
            <p className="mt-4 text-base text-[#5C5B5A]">
              Every feature we build strengthens this flow. Every job you do adds to the property's permanent record. No email chains. No lost PDFs. No searching Outlook.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2 text-center sm:grid-cols-4 md:grid-cols-9" data-testid="workflow-strip">
            {[
              { label: 'Property', tone: 'dark' },
              { label: 'Job created', tone: 'light' },
              { label: 'Tradies quote', tone: 'light' },
              { label: 'Quote approved', tone: 'light' },
              { label: 'Purchase order', tone: 'light' },
              { label: 'Photos & docs', tone: 'light' },
              { label: 'Invoice', tone: 'light' },
              { label: 'Job complete', tone: 'light' },
              { label: 'Passport updated', tone: 'accent' },
            ].map((step, i) => (
              <div
                key={i}
                className={`flex items-center justify-center border px-3 py-4 text-[11px] font-semibold uppercase tracking-[0.14em] ${
                  step.tone === 'dark' ? 'border-[#1C1B1A] bg-[#1C1B1A] text-white' :
                  step.tone === 'accent' ? 'border-[#D96C4E] bg-[#D96C4E] text-white' :
                  'border-[#E5E4E0] bg-white text-[#1C1B1A]'
                }`}
              >
                {step.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ WHY PROPERTY PASSPORT ============ */}
      <section className="border-b border-[#E5E4E0] bg-white py-20">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <div className="grid grid-cols-1 gap-16 lg:grid-cols-[1fr,1.2fr]">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#D96C4E]">Why Property Passport matters</div>
              <h2 className="mt-3 font-heading text-4xl font-bold tracking-tight text-[#1C1B1A] md:text-5xl">
                Every property deserves a service book.
              </h2>
              <p className="mt-5 text-base text-[#5C5B5A]">
                Cars have one. Houses don't. Every job done — roof replaced, drainage upgraded, asbestos removed, solar installed — becomes part of a permanent, searchable maintenance record.
              </p>
              <p className="mt-4 text-base text-[#5C5B5A]">
                Homeowners keep it private. Professionals see portfolio-wide views. Tradies get credit for every job they've done. The property's story travels with the address, forever.
              </p>
              <Link to="/passport" className="btn-primary mt-8 inline-flex" data-testid="learn-passport-btn">
                See Property Passport <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {[
                { icon: FileText, title: 'Maintenance timeline', body: 'Roof · plumbing · electrical · drainage · warranties · manuals — one searchable timeline per address.' },
                { icon: ShieldCheck, title: 'Compliance vault', body: 'Every tradie\'s licence, insurance and certification tracked with expiry alerts.' },
                { icon: ClipboardCheck, title: 'Full audit trail', body: 'Who did what, when, why — every quote, PO, invoice, photo attached to a job.' },
                { icon: Building2, title: 'Portfolio-wide reports', body: 'Strata & agencies see spend, alerts, and contractor performance across every property they manage.' },
              ].map((f, i) => (
                <div key={i} className="border border-[#E5E4E0] p-6" data-testid={`passport-feature-${i}`}>
                  <div className="flex h-10 w-10 items-center justify-center bg-[#1A362D] text-white">
                    <f.icon size={18} />
                  </div>
                  <h3 className="mt-4 font-heading text-lg font-bold text-[#1C1B1A]">{f.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#5C5B5A]">{f.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ POPULAR TRADES ============ */}
      <section className="border-b border-[#E5E4E0] bg-[#FAF9F6] py-16">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <div className="mb-8 flex items-end justify-between">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#1A362D]">Popular trades</div>
              <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-[#1C1B1A]">Start with what you need</h2>
            </div>
            <Link to="/search" className="text-sm font-semibold uppercase tracking-[0.15em] text-[#1A362D] hover:underline" data-testid="see-all-trades">
              See all →
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-8">
            {categories.map((c) => (
              <Link
                key={c.id}
                to={`/search?q=${c.name}`}
                className="border border-[#E5E4E0] bg-white p-4 text-center text-sm font-semibold text-[#1C1B1A] transition-colors hover:border-[#1A362D] hover:bg-[#1A362D] hover:text-white"
                data-testid={`popular-trade-${c.id}`}
              >
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="border-t border-[#E5E4E0] bg-[#1C1B1A] py-12 text-white">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-4">
            <div>
              <div className="font-heading text-xl font-bold">Trades &amp; Neighbours</div>
              <div className="mt-1 text-[11px] uppercase tracking-[0.24em] text-white/50">Australia's property OS</div>
              <p className="mt-4 max-w-xs text-sm text-white/70">One record. Every property. Every job. Every tradie. Every stakeholder — one workspace.</p>
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-white/40">Homeowners</div>
              <ul className="mt-3 space-y-2 text-sm">
                <li><Link to="/search" className="text-white/80 hover:text-white">Find a tradie</Link></li>
                <li><Link to="/saved" className="text-white/80 hover:text-white">Saved tradies</Link></li>
              </ul>
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-white/40">Tradies</div>
              <ul className="mt-3 space-y-2 text-sm">
                <li><Link to="/for-tradies" className="text-white/80 hover:text-white">Why list with us</Link></li>
                <li><Link to="/register" className="text-white/80 hover:text-white">Create free account</Link></li>
                <li><Link to="/tradie" className="text-white/80 hover:text-white">Tradie dashboard</Link></li>
              </ul>
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-white/40">Property Passport</div>
              <ul className="mt-3 space-y-2 text-sm">
                <li><Link to="/passport" className="text-white/80 hover:text-white">For professionals</Link></li>
                <li><Link to="/portfolio" className="text-white/80 hover:text-white">Portfolio dashboard</Link></li>
                <li><Link to="/agency/pricing" className="text-white/80 hover:text-white">Pricing</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-10 border-t border-white/10 pt-6 text-xs text-white/50">
            © {new Date().getFullYear()} Trades &amp; Neighbours. All rights reserved. Australia · Built for property professionals and everyday homeowners.
          </div>
        </div>
      </footer>
    </div>
  );
}
